import type { DisposalSet } from '@neptune/cx/utils';
import { PORTLET_NAME } from './legacy-links';

const RESOURCE_LIFECYCLE = '2';

interface SennaNavigation {
    form?: HTMLFormElement;
    path: string;
    replaceHistory: boolean;
}

type LiferayHost = {
    SPA?: {
        app?: {
            on(
                event: 'beforeNavigate',
                listener: (navigation: SennaNavigation, event: { preventDefault(): void }) => void,
            ): { removeListener(): void };
        };
    };
};

const withoutHash = (href: string): string => href.split('#')[0];

/**
 * @return the URL with the board's anchor added, or null when it leaves the board or already
 *         names a fragment of its own -- the answers list after a reply, say, which is the better
 *         place to land
 */
const anchorToBoard = (href: string, anchorId: string): string | null => {
    // A bare fragment resolves against the current URL, which on a board view names this portlet
    // too; anchoring it would turn an in-page control into a jump to the board.
    if (!href || href.startsWith('#')) {
        return null;
    }

    const url = new URL(href, document.baseURI);

    if (
        url.hash ||
        url.origin !== location.origin ||
        url.pathname !== location.pathname ||
        url.searchParams.get('p_p_id') !== PORTLET_NAME ||
        url.searchParams.get('p_p_lifecycle') === RESOURCE_LIFECYCLE
    ) {
        return null;
    }

    url.hash = anchorId;

    return url.toString();
};

/** @return the URL anchored to the board on this page, or the URL itself when it cannot be */
export const anchoredBoardHref = (href: string): string => {
    const anchorId = document.querySelector<HTMLElement>('.chroa-discussion-board__anchor')?.dataset
        .chroaFragment;

    return (anchorId && anchorToBoard(href, anchorId)) || href;
};

/**
 * Also opts the element out of Liferay SPA. The portlet is excluded from it, but only for links
 * inside the portlet's boundary, and the management toolbar renders its menus outside.
 *
 * Attributes rather than properties: a form with a field named "action" shadows form.action.
 */
const anchor = (element: Element, attribute: 'action' | 'href', anchorId: string): void => {
    const anchored = anchorToBoard(element.getAttribute(attribute) ?? '', anchorId);

    if (anchored) {
        element.setAttribute(attribute, anchored);
        element.setAttribute('data-senna-off', 'true');
    }
};

/**
 * Makes each anchor's fragment its id, so that jumps within the page find it.
 *
 * Only once the page has loaded: until then, the browser and Liferay SPA would each scroll to it
 * of their own accord, as discussion:anchor explains. Liferay SPA's load handler runs ahead of the
 * task queued here.
 */
const nameAnchorsOnceLoaded = (root: HTMLElement, gc: DisposalSet): void => {
    const nameAnchors = () => {
        for (const anchorEl of root.querySelectorAll<HTMLElement>('[data-chroa-fragment]')) {
            const fragment = anchorEl.dataset.chroaFragment;

            if (fragment) anchorEl.id = fragment;
        }
    };

    if (document.readyState === 'complete') {
        nameAnchors();
    } else {
        gc.register(window).addListener('load', () => window.setTimeout(nameAnchors));
    }
};

/**
 * Makes a navigation to a board view through Liferay SPA a full page load onto the board, as the
 * board's own links are. Clay's results bar clears its filters with buttons that navigate through
 * Liferay SPA whatever the portlet's exclusion from it, and so land at the top of the page.
 *
 * Left to Liferay SPA: a form post, whose body a load would drop; back, forward and refresh, whose
 * URL the address bar already shows, so that a load would add a history entry; and the view
 * already showing, where assigning the URL would only jump to its fragment.
 */
const loadBoardNavigations = (anchorId: string, gc: DisposalSet): void => {
    const app = (window as { Liferay?: LiferayHost }).Liferay?.SPA?.app;

    if (!app) {
        return;
    }

    const handle = app.on('beforeNavigate', (navigation, event) => {
        if (navigation.form || navigation.replaceHistory) {
            return;
        }

        const anchored = anchorToBoard(navigation.path, anchorId);

        if (anchored && withoutHash(anchored) !== withoutHash(location.href)) {
            event.preventDefault();
            location.assign(anchored);
        }
    });

    gc.register(() => handle.removeListener());
};

/**
 * Lands every navigation within the board on the board itself, rather than at the top of the page
 * under the site header.
 *
 * The board renders without Liferay SPA, so each view is a full page load, and discussion:anchor
 * scrolls it to the fragment. A POST keeps the anchor too: a redirect naming no fragment inherits
 * the one the form was submitted to.
 */
export const initBoardAnchor = (root: HTMLElement, gc: DisposalSet): void => {
    // Left by discussion:anchor when Liferay SPA put the page in. This runs once Liferay SPA has
    // finished scrolling. The last is where a full load would have ended up: a targeted message
    // lands after the board.
    const pendingEls = root.querySelectorAll<HTMLElement>('[data-chroa-land-pending]');
    const landingEl = pendingEls[pendingEls.length - 1];

    for (const pendingEl of pendingEls) {
        pendingEl.removeAttribute('data-chroa-land-pending');
    }

    if (landingEl) {
        landingEl.scrollIntoView();
        landingEl.setAttribute('data-chroa-landed', '');
    }

    nameAnchorsOnceLoaded(root, gc);

    const anchorEl = root.querySelector<HTMLElement>('.chroa-discussion-board__anchor');
    const anchorId = anchorEl?.dataset.chroaFragment;

    if (!anchorId) {
        return;
    }

    loadBoardNavigations(anchorId, gc);

    // Forms are anchored up front rather than as they submit, because most never fire a submit
    // event: they are posted by script, and Liferay's submitForm ends in form.submit(). Watched,
    // because the management toolbar may mount its search form after this runs.
    const anchorForms = (scope: Element) => {
        const forms = scope.matches('form[action]') ? [scope] : scope.querySelectorAll('form[action]');

        for (const form of forms) {
            anchor(form, 'action', anchorId);
        }
    };

    anchorForms(root);

    const observer = new MutationObserver((records) => {
        for (const record of records) {
            for (const node of record.addedNodes) {
                if (node instanceof Element) {
                    anchorForms(node);
                }
            }
        }
    });

    observer.observe(root, { childList: true, subtree: true });

    gc.register(observer);

    const anchorLink = (event: MouseEvent) => {
        const link = event.target instanceof Element ? event.target.closest('a[href]') : null;

        if (link) {
            anchor(link, 'href', anchorId);
        }
    };

    // Delegated from the document, because the management toolbar renders its menus outside the
    // board. Capturing, to run ahead of Liferay SPA's own click handling.
    gc.register(document)
        .addListener('click', anchorLink, true)
        .addListener('auxclick', anchorLink, true);
};
